import ResizeObserver from 'resize-observer-polyfill';
import {AttrsSchema, Component} from 'panel';
import type {GradientColorObject, PatternObject, Point} from 'highcharts';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import type {FormattedRow, LiftRow, RawCell} from 'common/report/retention/util/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {
  getLiftBaselineSegmentRawHead,
  getRetentionCurveTableColumnGroups,
  mapToRetentionCurveTableRows,
} from 'common/report/retention/util/parsing';
import {SegmentKeyToPath} from 'common/report/retention/models/types';
import type {LineChartAttrs, LineChartProps} from 'common/charts/visualization/line-chart';
import {DisplayAnalysisType} from 'common/charts/visualization/line-chart/enums';
import {ChartType, DEFAULT_CHART_WIDTH, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {IChartDataItem, Popover} from 'common/charts/visualization/line-chart/types';
import {XAxisUnit} from 'common/charts/visualization/line-chart/enums';
import {BookmarkData, RetentionBookmarkType} from 'common/report/retention/charts/types';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {handlePopoverAction, RetentionPopover} from 'common/report/retention/visualization/popover';
import {AGGREGATED_ROW_NAME} from 'common/report/retention/util/constants';
import {BookmarkController} from 'common/report/retention/bookmark-controller';
import {MultimetricBookmarkController} from 'common/report/retention/bookmark-controller/multimetric';
import {RetentionBookmarkController} from 'common/report/retention/bookmark-controller/retention';

import {getDataCellRenderer, template} from './template';
import css from './index.inline.styl';
import {computeTicks, getLineChartTooltip} from './line-chart-format';

type PopoverParams = {
  popover: RetentionPopover;
  title: string;
  subtitle: string;
  buttonOptions: Array<{label: string; value?: string; values?: TODO[]}>;
  preventCloseSelectors: string[];
  color: string | GradientColorObject | PatternObject;
  isAggregate: boolean;
  retainedCount: string;
  dataGroupName: string;
  dataGroupNamePlural: string;
  retainedPercent: string;
  dropOffCount: string;
  dropOffPercent: string;
};

interface LineChartState {
  chartRows: FormattedRow[];
  chartWidth: number; // px
  segmentKeyToPath?: SegmentKeyToPath;
  truncatedSegmentDisplayLookup?: Map<string, string>;
  formattedLiftRowsData: Map<string, LiftRow>;
  selectedHighchartsPoint?: Point;
  popoverOpen: boolean;
  popoverParams: PopoverParams;
  popoverStyles?: {top: string; left: string};
}

export interface RetentionCurveChartAttrs {
  'project-timezone'?: string;
  'utc-offset'?: number;
  'show-popover'?: boolean;
  'limit-displayed-segments'?: boolean;
  'segment-display-limit'?: number;
}

export type RetentionCurveChartProps = {
  bookmarkData: BookmarkData;
  popoverProp?: () => Popover;
};
export class RetentionCurveChart extends Component<
  LineChartState,
  RetentionCurveChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  public popoverProp?: () => Popover;

  formatTicksX = computeTicks();

  private _bookmarkController: BookmarkController;
  static get attrsSchema(): AttrsSchema<RetentionCurveChartAttrs> {
    return {
      'utc-offset': `number`,
      'project-timezone': `string`,
      'show-popover': `boolean`,
      'limit-displayed-segments': `boolean`,
      'segment-display-limit': `number`,
    };
  }

  public set bookmarkData(data: BookmarkData) {
    if (this._bookmarkController && this._bookmarkController.isEquivalent(data.bookmark, data.queryResults)) {
      return;
    }
    switch (data.type) {
      case RetentionBookmarkType.MULTIMETRIC:
        this._bookmarkController = new MultimetricBookmarkController(data.bookmark, data.queryResults);
        break;
      case RetentionBookmarkType.RETENTION:
        this._bookmarkController = new RetentionBookmarkController(data.bookmark, data.queryResults);
        break;
    }
    this.updateCurveChartData();
  }

  public chartResizeObserver: ResizeObserver;

  get config() {
    return {
      useShadowDom: true,
      css,
      contexts: [`currentProject` as const, `segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      defaultState: {
        popoverOpen: false,
        popoverParams: null,
        chartRows: [],
        chartWidth: DEFAULT_CHART_WIDTH, // px
        formattedLiftRowsData: new Map<string, LiftRow>(),
      },
      helpers: {
        hasResults: () => this._bookmarkController.hasResults(),

        getChartType: () => this._bookmarkController.getChartType(),

        isFormattingTicksX: () => this.state.chartWidth > DEFAULT_CHART_WIDTH,

        getProjectTimezone: () => this.attr(`project-timezone`) || this.getCurrentProject()?.timezone,

        getUtcOffset: () => this.attr(`utc-offset`) || this.getCurrentProject()?.utcOffset,

        getChartData: (): IChartDataItem[] => {
          const {segmentKeyToPath, chartRows, formattedLiftRowsData} = this.state;

          return chartRows
            .filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head))
            .map((row) => {
              const liftRow = formattedLiftRowsData?.get(row.raw_head);
              const data = (liftRow || row).data.map(
                (cell, idx) =>
                  [idx, this._bookmarkController.isPercentageView() ? cell.percent : cell.value] as [number, number],
              );

              const path = segmentKeyToPath && segmentKeyToPath[row.raw_head] ? segmentKeyToPath[row.raw_head] : [];

              return {
                id: row.raw_head,
                name: row.raw_head,
                data,
                color: row.selection_color,
                incompleteLength: getNumIncompleteCells(row),
                path,
              };

              function getNumIncompleteCells(row) {
                const incompleteIdx = row.data.findIndex((data) => data.incomplete);
                return incompleteIdx >= 0 ? row.data.length - incompleteIdx : 0;
              }
            });
        },
        getLineChartProps: (): Partial<LineChartProps> => {
          return {
            ...(this.helpers.isFormattingTicksX() ? {formatTicksX: this.formatTicksX} : {}),
            formatXAxisLabel: this._bookmarkController.bucketLabeler(),
            formatYAxisLabel: this._bookmarkController.percentOrAbsoluteLabel(),
            formatTooltip: getLineChartTooltip(this, this._bookmarkController),
            chartData: this.helpers.getChartData(),
            onHighChartClick: this.helpers.handleHighChartClick,
          };
        },

        getXAxisOptions: () => {
          return {unit: XAxisUnit.Time};
        },

        getYAxisOptions: () => {
          return this._bookmarkController.isPercentageView() ? {maxPadding: 0} : {};
        },

        getLineChartAttrs: (): Partial<LineChartAttrs> => ({
          'chart-width': null,
          'display-analysis-type': DisplayAnalysisType.Linear,
          'incomplete-to-date': this.helpers.isEndDateIncomplete(),
          timezone: this.helpers.getProjectTimezone(),
          'utc-offset': this.helpers.getUtcOffset(),
          'x-axis-options': this.helpers.getXAxisOptions(),
          'y-axis-options': this.helpers.getYAxisOptions(),
          'zoom-type': `x`,
        }),

        isEndDateIncomplete: () => {
          const selectedSegmentsRows = this.selectedSegmentsRows;
          if (selectedSegmentsRows.every((row) => row.data.length < 2)) {
            return false;
          }
          for (const row of selectedSegmentsRows) {
            for (const cell of row.data) {
              if (cell.incomplete) {
                return true;
              }
            }
          }
          return false;
        },

        closePopover: () => {
          deselectPoint(this.state.selectedHighchartsPoint);
          this.update({
            popoverOpen: false,
            selectedHighchartsPoint: null,
          });
        },

        handleHighChartClick: (point: Point) => {
          const {series, index, color} = point;
          const {name: segmentKey} = series;

          selectPoint(point);

          const left = series.chart.plotLeft + point.plotX + 12;
          const top = series.chart.plotTop + point.plotY + 12;
          const popoverStyles = {left: `${left}px`, top: `${top}px`};
          const row = this.state.chartRows.filter((row) => row.head === segmentKey);
          const selectedRow = row[0];
          const rowIdx = this.state.chartRows.indexOf(selectedRow);
          this.update({
            selectedHighchartsPoint: point,
            popoverStyles,
            popoverOpen: this.attr(`show-popover`),
            popoverParams: this.attr(`show-popover`)
              ? {
                  ...this.getPopoverParams({
                    selectedRow,
                    colIdx: index,
                    rowIdx,
                    color,
                  }),
                }
              : null,
          });
        },
      },
      template,
    };
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get selectedSegmentsRows() {
    return this.state.chartRows.filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head));
  }

  get selectedSegmentLiftRows() {
    return this.selectedSegmentsRows.map((row) => this.state.formattedLiftRowsData.get(row.raw_head));
  }

  private getPopoverParams({
    selectedRow,
    colIdx,
    rowIdx,
    color,
  }: {
    selectedRow: FormattedRow<RawCell>;
    colIdx: number;
    rowIdx: number;
    color: string | GradientColorObject | PatternObject;
  }) {
    const retentionType = this._bookmarkController.getRetentionType();
    const isAggregated =
      this._bookmarkController.getSegments().length > 0 || selectedRow.raw_head === AGGREGATED_ROW_NAME(retentionType);
    const cell = selectedRow.data[colIdx];
    const popover = this._bookmarkController.createPopover({
      projectTimezone: this.attr(`project-timezone`),
      colIdx,
      rowIdx,
      absoluteValue: cell.formatted_value,
      absoluteDropOffValue: cell.absolute_drop_off_value,
      cohortSize: cell.cohort_size,
      completeDate: cell.complete_date,
      enteredValue: cell.entered_value,
      enteredDate: cell.date,
      incomplete: cell.incomplete,
      isAggregated,
    });

    return {
      popover,
      title: popover.getTitle(),
      subtitle: popover.getSubtitle(),
      buttonOptions: popover.getButtonOptions(),
      preventCloseSelectors: [`.bar.hoverable`],
      color,
      isAggregate: popover.isAggregatedCell(),
      retainedCount: popover.getRetainedCount(),
      dataGroupName: popover.getDataGroupName(),
      dataGroupNamePlural: popover.getDataGroupNamePlural(),
      retainedPercent: selectedRow.data[colIdx].formatted_percent,
      dropOffCount: popover.getDropOffCount(),
      dropOffPercent: selectedRow.data[colIdx].percent_drop_off_value,
    };
  }

  handlePopoverActionClick = (ev) => {
    const {action} = ev.detail;
    handlePopoverAction({
      action,
      color: this.state.popoverParams.color as string,
      popover: this.state.popoverParams.popover,
    });
  };

  private hasRequiredParams(): boolean {
    return !!(this.isConnected && this._bookmarkController);
  }

  public updateCurveChartData() {
    if (this.hasRequiredParams()) {
      const timezone = this.helpers.getProjectTimezone();
      const {chartRows, formattedLiftRowsData, segmentKeyToPath} = this._bookmarkController.getCurveChartData(timezone);
      this.update({chartRows, formattedLiftRowsData, segmentKeyToPath});
      this.processData();
    }
  }

  liftDataForSegment(row: FormattedRow): {liftRow: LiftRow; baselineRow?: FormattedRow<RawCell>} {
    if (this._bookmarkController.getLiftComparison()?.groupValue) {
      const baselineRowKey = getLiftBaselineSegmentRawHead(
        row,
        this.state.segmentKeyToPath,
        this._bookmarkController.getLiftComparison(),
      );
      const baselineRow = this.state.chartRows.find((row) => row.raw_head === baselineRowKey);
      const liftRow = this.state.formattedLiftRowsData.get(row.raw_head);
      return {
        baselineRow,
        liftRow,
      };
    }
    return null;
  }

  processData() {
    const {chartRows, formattedLiftRowsData, segmentKeyToPath} = this.state;

    const tableRows = mapToRetentionCurveTableRows({
      formattedUiRows: chartRows,
      bookmarkController: this._bookmarkController,
      segmentKeyToPath,
      formattedLiftRowsData,
    });
    const tableRowLookup = new Map(tableRows.map((row) => [row.identifier, row]));

    const segmentFilterItems = chartRows.map((row) => ({
      label: row.raw_head,
      id: row.raw_head,
      color: row.selection_color,
      tableRow: tableRowLookup.get(row.raw_head),
    }));

    const tableColumns = getRetentionCurveTableColumnGroups({
      formattedUiRows: chartRows,
      bookmarkController: this._bookmarkController,
      getDataCellRenderer,
    });

    const maxNumSelections =
      this.attr(`segment-display-limit`) > 0
        ? this.attr(`segment-display-limit`)
        : REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTable].max;
    this.segmentFilterController.loadSections([{items: segmentFilterItems, tableColumns}], {
      defaultNumSelections: REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTable].default,
      maxNumSelections,
      tableSortConfig: this._bookmarkController.getSorting(),
    });
    this.segmentFilterController.loadFromSavedLegend(
      this._bookmarkController.getLegendSelectedSegments() && [
        {selected: this._bookmarkController.getLegendSelectedSegments().map((key) => ({key}))},
      ],
    );
    this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].query;
    this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].csvExport;
  }

  getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
  }

  connectedCallback() {
    this.chartResizeObserver = new ResizeObserver((entries) => {
      const {width} = entries[0].contentRect;
      this.update({
        chartWidth: width,
      });
    });
    super.connectedCallback();

    //TODO add store fetch
    this.updateCurveChartData();
  }

  disconnectedCallback() {
    this.chartResizeObserver.disconnect();
    super.disconnectedCallback();
  }
}

export const RetentionCurveChartEl = defineAndMakeJsxFactory<{
  props: RetentionCurveChartProps;
  attrs: RetentionCurveChartAttrs;
}>(`mp-retention-curve-chart`, RetentionCurveChart);

export const RetentionCurveChartReact = panel2react<{
  props: RetentionCurveChartProps;
  attrs: RetentionCurveChartAttrs;
}>(`mp-retention-curve-chart`, RetentionCurveChart);
